import { Col, Flex, Row, Select, TableProps, Tabs } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import Header from '../../../components/Header';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useGetMaterialReservationsQuery } from '../../../redux/api/material.ts';
import MaterialReservationsTable from '../../../components/MaterialReservationsTable/index.tsx';
import { MaterialReservation } from '../../../interfaces/MaterialInventory.interface.ts';
import MaterialReservationsGroupedTable from '../../../components/MaterialReservationsGroupedTable/index.tsx';
import { SorterResult } from 'antd/es/table/interface';

interface IQueryParams {
	page?: number;
	ordering?: string;
	availability_status?: string;
	vendors?: string;
	shape?: string;
	types?: string;
	material_cut_status?: string;
}

function MaterialReservations() {
	const location = useLocation();
	const navigate = useNavigate();
	const queryParams = new URLSearchParams(location.search);
	const currentPageUrl = Number(queryParams.get('page')) || 1;
	const ordering = queryParams.get('ordering') || '';
	const materialCutStatus = queryParams.get('material_cut_status') || 'All';

	const query: IQueryParams = {};

	if (currentPageUrl) {
		query.page = currentPageUrl;
	}

	if (ordering && ordering !== '') {
		query.ordering = ordering;
	}

	if (materialCutStatus !== 'All') {
		query.material_cut_status = materialCutStatus;
	}

	const { data, isLoading, refetch } = useGetMaterialReservationsQuery(query, {
		refetchOnFocus: true,
		refetchOnMountOrArgChange: true,
	});

	const refetchData = () => {
		refetch();
	};

	const [materialReservations, setMaterialReservations] = useState<
		MaterialReservation[]
	>([]);
	const [currentPage, setCurrentPage] = useState<number>(currentPageUrl);
	const [totalRecords, setTotalRecords] = useState<number>(0);
	const [pageReady, setPageReady] = useState(false);

	const pageChanged = async (page: number) => {
		queryParams.set('page', page.toString());
		navigate({ search: queryParams.toString() });
		setCurrentPage(page);
	};

	const handleCutStatusFilterChange = (value: string) => {
		if (value === 'All') {
			queryParams.delete('material_cut_status');
		} else {
			queryParams.set('material_cut_status', value);
		}
		queryParams.set('page', '1');
		setCurrentPage(1);
		navigate({ search: queryParams.toString() });
	};

	const handleTableChange: TableProps['onChange'] = (
		_pagination,
		_filters,
		sorter,
		extra
	) => {
		if (extra.action === 'sort') {
			const orderType = (sorter as SorterResult)?.order;
			const columnKey = (sorter as SorterResult)?.columnKey;
			const sorterColumn = (sorter as SorterResult)?.column;
			const column =
				orderType === 'ascend' || orderType === undefined
					? columnKey
					: '-' + columnKey;
			if (column !== undefined && sorterColumn !== undefined) {
				queryParams.set('ordering', column?.toString() as string);
			} else {
				queryParams.delete('ordering');
			}
			navigate({ search: queryParams.toString() });
		}
	};

	const tabs = [
		{
			key: 'individual',
			label: (
				<span data-testid="fastlane-material-reservations-tab-individual">
					Individual
				</span>
			),
			children: (
				<div
					data-testid="fastlane-material-reservations-tabpanel-individual"
					className="bg-white border-r border-l border-b w-full"
				>
					<MaterialReservationsTable
						data={materialReservations}
						currentPage={currentPage}
						recordsTotal={totalRecords}
						onChangePage={pageChanged}
						isLoading={isLoading}
						refetchData={refetchData}
						onChange={handleTableChange}
					/>
				</div>
			),
		},
		{
			key: 'grouped',
			label: (
				<span data-testid="fastlane-material-reservations-tab-grouped">
					Grouped
				</span>
			),
			children: (
				<div
					data-testid="fastlane-material-reservations-tabpanel-grouped"
					className="bg-white border-r border-l border-b w-full"
				>
					<MaterialReservationsGroupedTable />
				</div>
			),
		},
	];

	useEffect(() => {
		if (data) {
			setMaterialReservations(data?.results as MaterialReservation[]);
			setTotalRecords(data.count ? data.count : 0);
			setPageReady(true);
		}
	}, [data]);

	return (
		<main
			data-testid="page-fastlane-material-reservations"
			data-ready={pageReady ? 'true' : 'false'}
		>
			<Flex justify="space-between" align="center">
				<Header
					title="Material Reservations"
					icon={<PlusOutlined />}
					breadcrumbItems={[
						{
							title: <Link to="/material/materials">Material Inventory</Link>,
						},
						{
							title: 'Material Reservations',
						},
					]}
				/>
				<Select
					data-testid="fastlane-material-reservations-filter-cut-status"
					aria-label="Cut status"
					value={materialCutStatus}
					onChange={handleCutStatusFilterChange}
					options={[
						{
							value: 'All',
							label: (
								<span data-testid="fastlane-material-reservations-filter-cut-status-option-all">
									All
								</span>
							),
						},
						{
							value: 'Not Cut',
							label: (
								<span data-testid="fastlane-material-reservations-filter-cut-status-option-not-cut">
									Not Cut
								</span>
							),
						},
						{
							value: 'In Progress',
							label: (
								<span data-testid="fastlane-material-reservations-filter-cut-status-option-in-progress">
									In Progress
								</span>
							),
						},
						{
							value: 'Cut',
							label: (
								<span data-testid="fastlane-material-reservations-filter-cut-status-option-cut">
									Cut
								</span>
							),
						},
					]}
					style={{ width: 160 }}
				/>
			</Flex>
			<Row>
				<Col span={24}>
					<div className="flex flex-row gap-2 items-start mb-5">
						<Tabs
							defaultActiveKey="individual"
							items={tabs}
							size="small"
							type="card"
							tabBarStyle={{ marginBottom: 0 }}
							className="w-full"
						/>
					</div>
				</Col>
			</Row>
		</main>
	);
}

export default MaterialReservations;
